// Terms & Conditions for Interest To Income (iTi).
// Drafted as a reasonable starter — final wording should be reviewed by counsel.
import LegalLayout from "@/components/LegalLayout";

const LAST_UPDATED = "August 17, 2026";

export default function Terms() {
  return (
    <LegalLayout title="Terms & Conditions" updated={LAST_UPDATED} testId="terms-page">
      <p>
        These Terms &amp; Conditions (&ldquo;<b>Terms</b>&rdquo;) govern your access to and use of
        Interest To Income (&ldquo;<b>iTi</b>&rdquo;, &ldquo;<b>we</b>&rdquo;, &ldquo;<b>our</b>&rdquo;,
        or &ldquo;<b>us</b>&rdquo;), the website-builder and launch-plan service operated at
        interesttoincome.com from <b>5806 Grove Ave Ste 355, Richmond, Virginia, USA</b>. By creating
        an account or using any part of the Service, you agree to these Terms. If you do not agree,
        do not use the Service.
      </p>

      <h2>1. Eligibility &amp; account</h2>
      <p>
        You must be at least 18 years old and able to enter into a binding contract. You are
        responsible for keeping your account credentials secure and for everything that happens
        under your account. Notify us immediately of any unauthorized use.
      </p>

      <h2>2. Plans, billing &amp; renewals</h2>
      <ul>
        <li>
          <b>Free</b>: a 30-day free trial/access period to core features on a free platform
          subdomain. Free is <b>not</b> a recurring paid plan, requires no payment method, and does
          not include a custom domain.
        </li>
        <li>
          <b>Paid plans</b>: <b>Launch</b>, <b>Growth</b>, and <b>Scale</b> are auto-renewing
          subscriptions available on three billing intervals — <b>Monthly</b>, <b>Bi-Annual</b>
          (billed every 6 months), and <b>Annual</b> (billed every 12 months).
        </li>
        <li>
          <b>Current pricing</b>: Launch $25/month · $150 per 6 months · $225/year.
          Growth $45/month · $270 per 6 months · $405/year.
          Scale $99/month · $594 per 6 months · $891/year.
        </li>
        <li>
          <b>Annual promotional pricing</b> is 25% off the reference annual price ($300, $540, and
          $1,188 respectively). <b>Annual plans are non-refundable.</b>
        </li>
      </ul>

      <p><b>Custom domains.</b></p>
      <ul>
        <li>
          <b>Monthly plans do not include a free custom domain.</b> You may connect a domain you
          own or purchase one separately.
        </li>
        <li>
          <b>Bi-Annual and Annual plans include one free standard custom domain for your first
          website only</b>, granted after the qualifying payment. This benefit is
          <b> account-level</b> — one included domain per account, regardless of plan tier or how
          many websites you run.
        </li>
        <li>
          The included domain covers standard registrations up to a $25/year registrar cost.
          <b> Premium domains and .ai / .io domains are not included.</b> Domains beyond, or outside,
          the included entitlement are purchased separately.
        </li>
      </ul>

      <p><b>Website hosting.</b></p>
      <ul>
        <li><b>Hosting for your first website (Website #1) is included</b> with an active paid plan.</li>
        <li>
          <b>Each additional live website requires Additional Website Hosting at $50/month per
          website</b>, billed as a separate, independently cancelable subscription. Additional
          Website Hosting keeps that website publicly live — it does <b>not</b> create another
          account plan, domain entitlement, free trial, or content-generation allowance.
        </li>
        <li>
          If an Additional Website Hosting subscription lapses or becomes past due, the affected
          website may be temporarily paused until hosting is reactivated.
        </li>
      </ul>

      <p>
        <b>Legacy products.</b> Legacy $20/month extra-domain subscriptions are grandfathered
        behavior for existing subscribers only and are not offered to new customers.
      </p>
      <p>
        Subscriptions auto-renew until cancelled. Cancelling stops future billing — paid periods
        already started are non-refundable except where required by law. Payments are processed by
        Stripe, Inc.; you must comply with Stripe&apos;s terms.
      </p>
      <p>
        Free platform subdomains are only included on the Free plan; paid plans publish to custom
        domains. If a domain- or hosting-related subscription becomes past due, we may suspend the
        associated domain or website until payment is restored.
      </p>

      <h2>3. AI-generated content</h2>
      <p>
        iTi uses generative AI (OpenAI, Google Gemini, and other vendors) to produce website copy,
        images, and marketing plans. AI output is provided <b>as-is</b>. You are solely responsible
        for reviewing, editing, and ensuring the accuracy, legality, and suitability of any
        AI-generated content before publishing. We do not guarantee that AI output is original,
        non-infringing, factually correct, or fit for any particular purpose.
      </p>

      <h2>4. No guarantee of results — tools, not promises</h2>
      <p>
        <b>Interest To Income provides tools, templates, AI-generated suggestions, and educational
        guides — not a guarantee of business success.</b> Anything we generate (websites, marketing
        plans, audience research, 30-day action plans, AI imagery, product copy, pricing
        suggestions) is for your own consideration and use. You are responsible for:
      </p>
      <ul>
        <li>
          Verifying that any AI-generated copy, claims, statistics, prices, or business
          recommendations are accurate, legal, and appropriate for your situation.
        </li>
        <li>
          Doing your own due diligence on your market, customers, competitors, pricing, regulations,
          taxes, licensing, and any other operational decision.
        </li>
        <li>
          Operating your own business — including sales, fulfilment, customer service, refunds,
          legal compliance, and tax filing.
        </li>
      </ul>
      <p>
        <b>We do not warrant or guarantee that you will earn income, gain customers, or achieve any
        specific business outcome by using the Service.</b> Outcomes depend on countless factors
        outside our control (your market, effort, execution, timing, economy, etc.) and individual
        results will vary.
      </p>
      <p>
        <b>To the maximum extent permitted by law, you agree that we are not responsible or liable
        for any loss of income, loss of profits, loss of customers, loss of opportunity, loss of
        data, business interruption, reputational harm, or any other loss or damages — direct or
        indirect — that you or any third party may suffer from using, relying on, or being unable
        to use the Service, our outputs, or any of our products.</b> You use iTi at your own risk.
      </p>

      <h2>5. Your content</h2>
      <p>
        You retain ownership of the content you submit and the content your website publishes. By
        using the Service, you grant us a worldwide, royalty-free license to host, store, process,
        and display your content solely as needed to operate the Service (including via our hosting
        partners — Cloudflare, Vercel, and others).
      </p>

      <h2>6. Acceptable use</h2>
      <p>You agree not to use the Service to:</p>
      <ul>
        <li>Violate any law, regulation, or third-party right (including IP and privacy rights).</li>
        <li>Generate or publish content that is defamatory, hateful, sexual content involving minors, or otherwise harmful.</li>
        <li>Distribute malware, spam, or attempt to interfere with the Service&apos;s integrity.</li>
        <li>Sell prohibited or illegal goods/services through your iTi-hosted store.</li>
      </ul>
      <p>We may suspend or terminate accounts that violate these rules, with or without notice.</p>

      <h2>7. Custom domains &amp; payments to you</h2>
      <p>
        iTi integrates with Cloudflare for domain registration and Stripe Connect for payments you
        accept on your storefront. Funds you collect are paid out to your own Stripe account, minus
        any platform fee disclosed at the time of sale. We are not a party to transactions between
        you and your customers and disclaim liability for those transactions.
      </p>

      <h2>8. Cancellation, suspension &amp; refunds</h2>
      <p>
        You can cancel at any time from your billing settings. We may suspend or terminate the
        Service immediately for material breach. Except where required by law, fees are
        non-refundable. Pro-rata refunds may be offered at our discretion in good faith for genuine
        billing errors.
      </p>

      <h2>9. Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT WARRANTIES OF
        ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
        AND NON-INFRINGEMENT. We do not warrant that the Service will be uninterrupted, error-free,
        or generate any particular business outcome.
      </p>

      <h2>10. Limitation of liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR AGGREGATE LIABILITY ARISING OUT OF OR RELATED
        TO THE SERVICE WILL NOT EXCEED THE GREATER OF (a) USD $100 OR (b) THE AMOUNTS YOU PAID US
        IN THE TWELVE MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM. WE WILL NOT BE LIABLE FOR
        INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS,
        LOST REVENUE, LOST INCOME, LOST DATA, OR LOST GOODWILL — REGARDLESS OF THE THEORY OF
        LIABILITY AND EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
      </p>

      <h2>11. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless iTi and its affiliates, officers, employees, and
        agents from any claims, damages, losses, liabilities, costs, and expenses (including
        reasonable attorneys&apos; fees) arising out of your content, your use of the Service, your
        published websites, your products and services, or your violation of these Terms.
      </p>

      <h2>12. Changes</h2>
      <p>
        We may update these Terms by posting a new version. Material changes will be flagged in-app
        or by email. Continued use after the effective date means you accept the updates.
      </p>

      <h2>13. Governing law &amp; venue</h2>
      <p>
        These Terms are governed by the laws of the <b>Commonwealth of Virginia, United States of
        America</b>, without regard to its conflict-of-laws rules. The exclusive venue for any
        dispute will be the state and federal courts located in <b>Richmond, Virginia</b>, and you
        consent to the personal jurisdiction of those courts.
      </p>

      <h2>14. Contact</h2>
      <p>
        Interest To Income<br />
        5806 Grove Ave Ste 355<br />
        Richmond, Virginia, USA<br />
        Email: <a href="mailto:hello@interesttoincome.com">hello@interesttoincome.com</a>
      </p>
    </LegalLayout>
  );
}
